/**
 * Force Logout Utility
 * 
 * Centralized helper for force-logout operations (single-session conflicts, integrity failures, etc.)
 * 
 * This helper performs a complete logout:
 * - Clears React Query cache
 * - Clears all auth-related localStorage keys
 * - Clears sessionStorage
 * - Clears axios default headers
 * - Redirects to login page
 * 
 * Usage:
 * ```ts
 * import { createForceLogout } from '../utils/forceLogout';
 * import { useQueryClient } from '@tanstack/react-query';
 * 
 * const queryClient = useQueryClient();
 * const forceLogout = createForceLogout(queryClient);
 * 
 * // Later...
 * forceLogout('single-session-conflict', 'test');
 * ```
 */

import { QueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/apiClient';
import { getCompanySlugFromLocation, resolveActiveCompanySlug, getRuntimeEnvFromHostname } from './companySlug';
import { clearAuthSnapshot } from '../prefetch/api';
import { isSuperadminPath } from './routeHelpers';

/**
 * ALLOWED LOGOUT REASONS:
 * - Manual logout: 'manual_click', 'user_logout', 'manual'
 * - Blocked users: 'blocked', 'user-blocked' (valid auto-logout scenario)
 * - Inactivity timeout: 'inactivity_timeout' (valid auto-logout for security)
 * - Session expired: 'session-expired' (valid auto-logout when SESSION_REVOKED is detected)
 * All other reasons (session conflict, token expired, etc.) are BLOCKED
 */
const ALLOWED_LOGOUT_REASONS = [
  'manual_click',
  'user_logout',
  'manual', // Legacy support
  'blocked', // BLOCKED user flow - valid auto-logout
  'user-blocked', // Alternative name for blocked
  'inactivity_timeout', // Inactivity timeout - valid auto-logout for security
  'session-expired', // Session expired (SESSION_REVOKED) - valid auto-logout
  'cross_tab_logout', // Cross-tab logout sync - valid auto-logout when another tab logs out
] as const;

/**
 * Keys to remove from localStorage during force logout
 * These are all auth-related keys that should be cleared
 * CRITICAL: Must include all keys that store auth/company state
 */
const AUTH_STORAGE_KEYS = [
  'token',
  'accessToken',
  'currentCompany',
  'currentCompanySlug',
  'activeTenantId',
  'activeTenantName',
  'activeRole',
  'lastLogin',
  'lastActivity',
  'client',
  'lang', // Per-company language; must be cleared so new company loads its saved language
  'rememberEmail',
  'rememberPassword',
  'wjs_active_company_v2', // New company slug storage key
  'wjs_superadmin_auth', // Superadmin-specific auth (if exists)
] as const;

/**
 * Logout broadcast key - used to signal other tabs that a logout occurred
 * When this key changes in localStorage, other tabs will receive a storage event
 * and can trigger their own logout
 */
export const LOGOUT_BROADCAST_KEY = 'wjs_lastLogoutAt';

/**
 * Creates a force logout function that can be used to immediately log out a user
 * 
 * @param queryClient - React Query client instance (required for clearing cache)
 * @returns A function that performs force logout: (reason?: string, companySlug?: string) => void
 */
/**
 * Force logout reason types
 */
export type ForceLogoutReason =
  | 'manual'
  | 'manual_click'
  | 'user_logout'
  | 'auth_failed'
  | 'superadmin_auth_failed'
  | 'socket_unauthorized'
  | 'integrity_failed'
  | 'other_session_active'
  | 'inactivity_timeout'
  | 'missing_company_client'
  | 'token-invalid'
  | 'integrity-mismatch'
  | 'single-session-conflict'
  | 'unknown';

/**
 * Force logout options
 */
export interface ForceLogoutOptions {
  reason?: ForceLogoutReason | string;
  source?: string;
  errorCode?: string | number;
  errorMessage?: string;
  companySlug?: string;
  [key: string]: any;
}

/**
 * Debug helper to log force logout with full context
 */
function debugForceLogout(reason: string, extra?: any) {
  const pathname = typeof window !== 'undefined' ? window.location.pathname : 'N/A';
  const isSuperadmin = typeof window !== 'undefined' ? isSuperadminPath(pathname) : false;
  
  // Get stack trace to identify caller
  const stack = new Error().stack;
  const caller = stack?.split('\n')[3]?.trim() || 'unknown';
  
  console.warn('[ForceLogout] Triggered:', {
    reason: reason || 'unknown',
    pathname,
    isSuperadmin,
    caller: caller.substring(0, 150),
    extra,
    timestamp: new Date().toISOString(),
  });
  
  // Also log full stack in development
  if (process.env.NODE_ENV === 'development' && stack) {
    console.warn('[ForceLogout] Full stack trace:', stack.split('\n').slice(0, 10));
  }
}

export function createForceLogout(queryClient: QueryClient) {
  return (reasonOrOpts?: string | ForceLogoutOptions, companySlug?: string) => {
    // Support both old API (reason, companySlug) and new API (options object)
    let opts: ForceLogoutOptions;
    if (typeof reasonOrOpts === 'string') {
      // Legacy API: (reason: string, companySlug?: string)
      opts = { reason: reasonOrOpts as ForceLogoutReason, companySlug };
    } else {
      // New API: (options: ForceLogoutOptions)
      opts = reasonOrOpts || {};
      if (companySlug) {
        opts.companySlug = companySlug;
      }
    }
    
    const reason = opts.reason || 'unknown';
    
    // 🔥 CRITICAL: ONLY allow manual logout, blocked user logout, or inactivity timeout
    // This must be checked BEFORE any logout logic (clearing cache, localStorage, redirect, etc.)
    if (!reason || !ALLOWED_LOGOUT_REASONS.includes(reason as any)) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[ForceLogout][AUTO-LOGOUT-BLOCKED] Auto logout blocked. Only manual logout, blocked user logout, or inactivity timeout allowed:', {
          reason,
          source: opts.source,
          pathname: typeof window !== 'undefined' ? window.location.pathname : 'N/A',
          allowedReasons: ALLOWED_LOGOUT_REASONS,
          note: 'Auto logout is disabled except for BLOCKED users or inactivity timeout. User must click Logout button to logout.',
        });
      }
      // DO NOT clear token, DO NOT redirect, DO NOT clear cache.
      // Just return early and ignore the logout request completely.
      return;
    }
    
    const pathname = typeof window !== 'undefined' ? window.location.pathname : 'N/A';
    const isSuperadminRoute = typeof window !== 'undefined' ? isSuperadminPath(pathname) : false;
    
    // 🔥 CRITICAL: Log with full context
    debugForceLogout(reason, {
      ...opts,
      pathname,
      isSuperadminRoute,
    });
    
    // 🔥 GLOBAL FUSE: On superadmin routes, ONLY allow logout for:
    // - explicit manual logout (including manual_click, user_logout, manual)
    // - direct superadmin auth failure (401/403 from /api/superadmin/...)
    if (isSuperadminRoute) {
      const allowedReasons: ForceLogoutReason[] = [
        'manual',
        'manual_click', // Allow manual_click for superadmin logout (from Header/Sidebar buttons)
        'user_logout',  // Allow user_logout for superadmin logout
        'superadmin_auth_failed',
      ];
      
      if (!allowedReasons.includes(reason as ForceLogoutReason)) {
        console.warn(
          '[ForceLogout] BLOCKED on superadmin route – ignoring logout call',
          { 
            reason, 
            pathname, 
            opts,
            allowedReasons,
            caller: new Error().stack?.split('\n')[3]?.trim() || 'unknown',
          },
        );
        return; // ⛔ DO NOT LOG OUT
      }
    }
    
    // Store logout reason BEFORE clearing sessionStorage
    // This allows the login page to show the appropriate message
    if (reason && typeof window !== 'undefined') {
      try {
        // Store in sessionStorage before we clear it
        sessionStorage.setItem('logoutReason', reason);
      } catch (err) {
        console.error('[ForceLogout] Error storing logout reason:', err);
      }
    }
    
    // Use companySlug from opts if available
    const finalCompanySlug = opts.companySlug || companySlug;
    
    // 1. Clear React Query cache
    try {
      queryClient.clear();
    } catch (err) {
      console.error('[ForceLogout] Error clearing React Query cache:', err);
    }
    
    // 2. Clear auth snapshot (in-memory auth state)
    try {
      clearAuthSnapshot();
    } catch (err) {
      console.error('[ForceLogout] Error clearing auth snapshot:', err);
    }
    
    // 3. Clear all auth-related localStorage keys
    try {
      const oldClient = localStorage.getItem('client');
      if (oldClient) {
        try {
          JSON.parse(oldClient);
        } catch (e) {
          console.warn('[ForceLogout] Could not parse client data');
        }
      }
      AUTH_STORAGE_KEYS.forEach(key => {
        localStorage.removeItem(key);
      });
      
      // 3.5) Broadcast logout to other tabs via localStorage
      // This triggers a storage event in other tabs, allowing them to detect the logout
      try {
        const now = Date.now();
        localStorage.setItem(LOGOUT_BROADCAST_KEY, String(now));
      } catch (e) {
        // Ignore storage errors (e.g., quota exceeded)
        console.warn('[ForceLogout] Could not broadcast logout to other tabs:', e);
      }
      
      // 3.6) CRITICAL: Clear cookies that might persist
      // Use centralized auth cookie clear helper so flags (domain, secure, sameSite)
      // match the backend's setAuthCookie/clearAuthCookie behavior.
      if (typeof document !== 'undefined') {
        import('./authCookies').then(({ clearAllAuthCookies }) => {
          clearAllAuthCookies();
        }).catch(err => {
          console.error('[ForceLogout] Error importing authCookies:', err);
        });
      }
    } catch (err) {
      console.error('[ForceLogout] Error clearing localStorage:', err);
    }
    
    // 4. Clear sessionStorage (but keep logoutReason - we'll clear it on login page)
    try {
      // Save logoutReason before clearing
      const savedReason = reason || (typeof window !== 'undefined' ? sessionStorage.getItem('logoutReason') : null);
      sessionStorage.clear();
      // Restore logoutReason after clearing
      if (savedReason && typeof window !== 'undefined') {
        sessionStorage.setItem('logoutReason', savedReason);
      }
    } catch (err) {
      console.error('[ForceLogout] Error clearing sessionStorage:', err);
    }
    
    // 5. Clear axios default headers
    try {
      delete apiClient.defaults.headers.common['Authorization'];
    } catch (err) {
      console.error('[ForceLogout] Error clearing axios headers:', err);
    }
    
    // 6. Determine redirect target based on current route type
    // CRITICAL: For superadmin routes, always redirect to /superadmin/login
    if (typeof window !== 'undefined') {
      const pathname = window.location.pathname;
      const isSuperadminRoute = isSuperadminPath(pathname);
      
      // Include logout reason in URL as backup (sessionStorage might be cleared by browser)
      const reasonParam = reason ? `?logoutReason=${encodeURIComponent(reason)}` : '';
      
      // For superadmin routes, always redirect to superadmin login
      if (isSuperadminRoute) {
        window.location.href = `/superadmin/login${reasonParam}`;
        return;
      }
      
      // For company routes, use provided companySlug or extract from URL
      let finalCompany: string | undefined = finalCompanySlug;
      
      if (!finalCompany) {
        // Use centralized helper - URL is always source of truth
        const slugFromUrl = getCompanySlugFromLocation();
        const env = getRuntimeEnvFromHostname();
        finalCompany = slugFromUrl || resolveActiveCompanySlug({ env }) || undefined;
      }
      
      // Redirect to company login if we have a company slug
      if (finalCompany) {
        window.location.href = `/${finalCompany}/login${reasonParam}`;
      } else {
        // No company slug - redirect to root (main landing page)
        window.location.href = `/${reasonParam ? reasonParam : ''}`;
      }
    }
  };
}

